/**
 * Shared presentational pieces.
 *
 * Deliberately small and unclever: the visual language lives in styles.css (lifted from the
 * storefront), and these are just the handful of shapes every screen needs — loading, empty,
 * error, status, copyable code, and the show-once token modal.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError } from "../api/client.js";
import type { DeploymentStatus } from "../api/types.js";

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span className="row" role="status">
      <span className="spinner" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="center-screen">
      <span className="spinner" aria-hidden="true" />
      <p className="muted mono">{label}</p>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Banner({
  kind = "info",
  title,
  children,
}: {
  kind?: "info" | "warn" | "error";
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={`banner banner--${kind}`} role={kind === "error" ? "alert" : undefined}>
      {title ? <strong className="banner__title">{title}</strong> : null}
      {children}
    </div>
  );
}

/**
 * Customer-facing error. Shows our copy plus the request id, never provider text — §23.3 and
 * API-CONTRACT §16.2. The request id is the only thing support needs.
 */
export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const api = error instanceof ApiError ? error : null;
  const message = api ? api.customerMessage : "Something went wrong.";
  return (
    <Banner kind="error" title="That did not work">
      <p style={{ margin: 0 }}>{message}</p>
      {api?.requestId ? (
        <p className="mono small muted" style={{ marginTop: 8 }}>
          Reference {api.requestId}
        </p>
      ) : null}
      {onRetry ? (
        <div className="btn-row" style={{ marginTop: 14 }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
    </Banner>
  );
}

const STATUS_COPY: Record<DeploymentStatus, { label: string; tone: "ok" | "warn" | "bad" | "idle" }> = {
  AWAITING_PAYMENT: { label: "Awaiting checkout", tone: "warn" },
  QUEUED: { label: "Getting started", tone: "idle" },
  PROVISIONING: { label: "Building", tone: "idle" },
  PROVISIONING_FAILED: { label: "Needs attention", tone: "bad" },
  VERIFYING: { label: "Verifying", tone: "idle" },
  VERIFYING_FAILED: { label: "Needs attention", tone: "bad" },
  READY: { label: "Ready", tone: "ok" },
  SUSPENDED: { label: "Paused: billing", tone: "warn" },
  CANCELLATION_PENDING: { label: "Ending soon", tone: "warn" },
  RETAINED: { label: "Retained", tone: "warn" },
  DELETION_PENDING: { label: "Deleting", tone: "bad" },
  DELETED: { label: "Deleted", tone: "bad" },
};

/** Customer-facing status word. Never the raw enum — that is operator vocabulary. */
export function StatusPill({ status }: { status: DeploymentStatus }) {
  const copy = STATUS_COPY[status] ?? { label: "Unknown", tone: "idle" as const };
  return <span className={`pill pill--${copy.tone}`}>{copy.label}</span>;
}

/** Copy-to-clipboard with an honest fallback when the clipboard API is unavailable. */
export function useCopy(): [boolean, (text: string) => void] {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = useCallback((text: string) => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  }, []);
  return [copied, copy];
}

export function CodeBlock({ code, label = "Copy" }: { code: string; label?: string }) {
  const [copied, copy] = useCopy();
  return (
    <div className="copyable">
      <pre className="code">{code}</pre>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => copy(code)}>
        {copied ? "Copied" : label}
      </button>
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  dismissible = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  dismissible?: boolean;
}) {
  useEffect(() => {
    if (!dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dismissible, onClose]);

  return (
    <div className="modal-veil" onMouseDown={dismissible ? onClose : undefined}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The show-once credential modal (§12.3 "show it once").
 *
 * It is deliberately awkward to dismiss: closing is the moment the token becomes unrecoverable,
 * so the button says so and the modal ignores backdrop clicks and Escape.
 */
export function TokenModal({
  title,
  token,
  mcpConfig,
  endpointUrl,
  onClose,
}: {
  title: string;
  token: string;
  mcpConfig?: unknown;
  endpointUrl?: string;
  onClose: () => void;
}) {
  const [copiedToken, copyToken] = useCopy();
  const configText = useMemo(
    () => (mcpConfig ? JSON.stringify(mcpConfig, null, 2) : null),
    [mcpConfig],
  );

  return (
    <Modal title={title} onClose={onClose} dismissible={false}>
      <p className="modal__id">One-time credential</p>
      <h2>{title}</h2>
      <Banner kind="warn">
        This is the only time this key is shown. We keep a fingerprint of it, never the key
        itself, so if you lose it, you rotate it rather than look it up.
      </Banner>

      <label className="field">
        <span className="field__label">Your key</span>
        <div className="token">{token}</div>
      </label>
      <div className="btn-row" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn--primary btn--sm" onClick={() => copyToken(token)}>
          {copiedToken ? "Copied" : "Copy key"}
        </button>
      </div>

      {endpointUrl ? (
        <label className="field">
          <span className="field__label">Endpoint</span>
          <div className="mono muted">{endpointUrl}</div>
        </label>
      ) : null}

      {configText ? (
        <label className="field">
          <span className="field__label">MCP client configuration</span>
          <CodeBlock code={configText} label="Copy config" />
          <span className="field__hint">
            Paste this into your agent's MCP configuration file. It already contains the key above.
          </span>
        </label>
      ) : null}

      <div className="btn-row">
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          I have saved it, close
        </button>
      </div>
    </Modal>
  );
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "not recorded";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "not recorded";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return "not recorded";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "not recorded";
  return d.toLocaleDateString(undefined, { dateStyle: "long" });
}
